import { useEffect, RefObject } from 'react';

interface UseScrollSyncProps {
  scrollContainerRef: RefObject<HTMLDivElement>;
  bottomScrollbarRef: RefObject<HTMLDivElement>;
  milestonesData: any;
}

/**
 * Custom hook to sync horizontal scroll between table and bottom scrollbar
 */
export const useScrollSync = ({
  scrollContainerRef,
  bottomScrollbarRef,
  milestonesData
}: UseScrollSyncProps) => {
  // Sync table scroll to bottom scrollbar
  const handleTableScroll = () => {
    if (scrollContainerRef.current && bottomScrollbarRef.current) {
      bottomScrollbarRef.current.scrollLeft =
        scrollContainerRef.current.scrollLeft;
    }
  };

  // Sync bottom scrollbar to table scroll
  const handleBottomScrollbarScroll = () => {
    if (scrollContainerRef.current && bottomScrollbarRef.current) {
      scrollContainerRef.current.scrollLeft =
        bottomScrollbarRef.current.scrollLeft;
    }
  };

  // Update scrollbar width to match table width
  useEffect(() => {
    const updateScrollbarWidth = () => {
      if (scrollContainerRef.current && bottomScrollbarRef.current) {
        const scrollWidth = scrollContainerRef.current.scrollWidth;
        const scrollbarContent = bottomScrollbarRef.current
          .firstElementChild as HTMLElement;
        if (scrollbarContent) {
          scrollbarContent.style.width = `${scrollWidth}px`;
          scrollbarContent.style.minWidth = `${scrollWidth}px`;
        }
      }
    };

    // Multiple timing strategies to ensure table is fully rendered
    const timeouts = [
      setTimeout(updateScrollbarWidth, 0),
      setTimeout(updateScrollbarWidth, 100),
      setTimeout(updateScrollbarWidth, 500)
    ];

    // Update on window resize
    window.addEventListener('resize', updateScrollbarWidth);

    // Use ResizeObserver to detect when table size changes
    const observer = new ResizeObserver(updateScrollbarWidth);
    if (scrollContainerRef.current) {
      observer.observe(scrollContainerRef.current);
    }

    return () => {
      timeouts.forEach(clearTimeout);
      window.removeEventListener('resize', updateScrollbarWidth);
      observer.disconnect();
    };
  }, [milestonesData, scrollContainerRef, bottomScrollbarRef]);

  return {
    handleTableScroll,
    handleBottomScrollbarScroll
  };
};
